import { FC, Fragment, useLayoutEffect, useRef, useState } from 'react';
import { MdVolumeOff } from 'react-icons/md';
import { Motion, spring } from 'react-motion';
// Components
import { Button } from 'components/Buttons';
import VideoTimeline from './VideoTimeline';

type Props = {
  isQuestionVideo?: boolean;
  videoClasses?: string;
  videoUrl: string;
  onLoadCallback?: () => void;
  onEndedCallback?: () => void;
}

const rootVideoElem = document.getElementById('rootVideo') as HTMLVideoElement;

const Video:FC<Props> = ({
  // Props
  videoClasses = 'w-full', videoUrl, onLoadCallback, onEndedCallback, isQuestionVideo
}) => {
  const timeout = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [ buttonShown, setButtonShown ] = useState(false);
  const [ duration, setDuration ] = useState<number>(0);

  useLayoutEffect(() => {
    const container = containerRef.current;

    const sourceElem = document.createElement('source');
    sourceElem.src = videoUrl;

    rootVideoElem.className = videoClasses;
    rootVideoElem.appendChild(sourceElem);

    rootVideoElem.muted = false;

    rootVideoElem.load();

    if ( onLoadCallback ) rootVideoElem.onload = onLoadCallback;

    const promise = rootVideoElem.play();

    if ( typeof promise !== 'undefined' ){
      promise
        .catch(() => {
          rootVideoElem.muted = true;

          setButtonShown(true);
        })
        .finally(() => {
          if ( container ) container.appendChild(rootVideoElem);

          timeout.current = setTimeout(() => {
            rootVideoElem.play();
          }, 100);
        })
    }

    rootVideoElem.onended = onEnded;

    return () => {
      if ( timeout.current ) clearTimeout(timeout.current);
      if ( container ) container.removeChild(rootVideoElem);

      rootVideoElem.removeChild(sourceElem);
      rootVideoElem.className = 'hidden';

      document.body.appendChild(rootVideoElem);
    }
    // eslint-disable-next-line
  }, []);

  const handleClick = () => {
    rootVideoElem.muted = false;

    setButtonShown(false);
  }

  const onEnded = () => {
    if ( buttonShown ) setButtonShown(false);
    if ( onEndedCallback ) onEndedCallback();
    setDuration(0);
  }

  useLayoutEffect(() => {
    if (!isQuestionVideo) return;

    if (rootVideoElem) {
      // for some reason on iOS 16.3+ the video does not start from the beginning
      rootVideoElem.currentTime = 0.01;
      rootVideoElem.addEventListener('timeupdate', updateCountdown);
    }

    return () => {
      rootVideoElem.removeEventListener('timeupdate', updateCountdown);
    }
    // eslint-disable-next-line
  }, [rootVideoElem])

  function updateCountdown() {
    setDuration(rootVideoElem.currentTime / rootVideoElem.duration * 100);
  }

  return (
    <Fragment>
      {buttonShown ? (
        <div className="absolute left-0 top-0 m-4 z-10">
          <Button
            onClick={handleClick}
            disableShadow={true}
            size="small"
          ><MdVolumeOff /></Button>
        </div>
      ) : null}
      {isQuestionVideo ? (
        <div className="bg-black relative w-full pt-56.25%">
          <div className="absolute top-0 left-0 w-full h-full" ref={containerRef} />
          {!!duration && (
            <Motion style={{ smoothProgress: spring(duration) }}>
              {values => <VideoTimeline progress={values.smoothProgress} /> }
            </Motion>
          )}
        </div>
      ) : (
        <div className="bg-black w-full" ref={containerRef} />
      )}
    </Fragment>
  )
}

export default Video;
